import api from '../api/axios';

// ============ Types ============

export interface CapacitorCurve {
  id: number;
  name: string;
  guid: string;
  minValue: number;
  maxValue: number;
  bezierPoints: string | null;
}

export interface ShipControllerCurve {
  id: number;
  shipId: number;
  controllerType: 'Flight' | 'Shield';
  regenCurveGuid: string | null;
  regenNavCurveGuid: string | null;
  resistanceCurveGuid: string | null;
  capacitorMax: number;
  // Resolved curve data
  regenCurve?: CapacitorCurve;
  regenNavCurve?: CapacitorCurve;
  resistanceCurve?: CapacitorCurve;
}

export interface ShipPowerPool {
  id: number;
  shipId: number;
  weaponPoolSize: number;
  weaponRegenFillRate: number;
  weaponDecayRate: number;
  shieldPoolSize: number;
  shieldMaxItems: number;
  flightPoolSize: number;
  flightMaxItems: number;
  flightDecayRate: number;
}

export interface ShipEngineeringData {
  shipId: number;
  shipName: string;
  powerPool: ShipPowerPool | null;
  controllers: ShipControllerCurve[];
  hasEngineeringData: boolean;
}

export interface HardpointOverride {
  hardpointId: number;
  hardpointName?: string;
  itemDefinitionId: number;
  itemName?: string;
}

export interface UserLoadout {
  id: number;
  userId: string;
  name: string;
  description: string | null;
  shipId: number;
  shipName?: string;
  weaponPips: number;
  shieldPips: number;
  enginePips: number;
  isPublic: boolean;
  isDefault: boolean;
  shareCode: string | null;
  createdAt: string;
  updatedAt: string;
  hardpointOverrides: HardpointOverride[];
}

export interface CreateLoadoutDto {
  name: string;
  description?: string;
  shipId: number;
  weaponPips: number;
  shieldPips: number;
  enginePips: number;
  isPublic?: boolean;
  isDefault?: boolean;
  hardpointOverrides?: {
    hardpointId: number;
    itemDefinitionId: number;
  }[];
}

export interface UpdateLoadoutDto {
  name?: string;
  description?: string;
  weaponPips?: number;
  shieldPips?: number;
  enginePips?: number;
  isPublic?: boolean;
  isDefault?: boolean;
  hardpointOverrides?: {
    hardpointId: number;
    itemDefinitionId: number;
  }[];
}

export interface CompatibleItem {
  id: number;
  name: string;
  itemType: string;
  size: number;
  grade: number;
  manufacturer: string;
  weaponStats?: {
    damagePerSecond: number;
    alphaDamage: number;
    fireRate: number;
    range: number;
  };
  shieldStats?: {
    shieldHealthMax: number;
    regenRate: number;
  };
}

// ============ Capacitor Curve Calculation ============

/**
 * Calculate output value from a capacitor curve based on pip allocation (0-1)
 * This replicates the game's bezier curve interpolation
 */
export function calculateCurveOutput(curve: CapacitorCurve, pipValue: number): number {
  // Clamp pip value between 0 and 1
  const t = Math.max(0, Math.min(1, pipValue));
  
  // Linear interpolation between min and max
  // TODO: Implement proper bezier curve interpolation when bezierPoints are available
  return curve.minValue + (curve.maxValue - curve.minValue) * t;
}

/**
 * Calculate the effective regen rate for a controller based on current pip allocation
 */
export function calculateRegenRate(
  controller: ShipControllerCurve,
  pipValue: number,
  isNavMode: boolean,
  curves: CapacitorCurve[]
): number {
  const curveGuid = isNavMode ? controller.regenNavCurveGuid : controller.regenCurveGuid;
  if (!curveGuid) return 0;
  
  const curve = curves.find(c => c.guid === curveGuid);
  if (!curve) return 0;
  
  return calculateCurveOutput(curve, pipValue);
}

// ============ API Service Functions ============

export const loadoutService = {
  /**
   * Get all capacitor curves (for local calculations)
   */
  async getCapacitorCurves(): Promise<CapacitorCurve[]> {
    const response = await api.get('/loadouts/capacitor-curves');
    return response.data;
  },

  /**
   * Get engineering data for a specific ship
   */
  async getShipEngineering(shipId: number): Promise<ShipEngineeringData> {
    const response = await api.get(`/loadouts/ships/${shipId}/engineering`);
    return response.data;
  },

  /**
   * Get current user's loadouts
   */
  async getMyLoadouts(): Promise<UserLoadout[]> {
    const response = await api.get('/loadouts/my');
    return response.data;
  },

  /**
   * Get a specific loadout by ID
   */
  async getLoadout(loadoutId: number): Promise<UserLoadout> {
    const response = await api.get(`/loadouts/${loadoutId}`);
    return response.data;
  },

  /**
   * Get a loadout by share code (public access)
   */
  async getLoadoutByShareCode(shareCode: string): Promise<UserLoadout> {
    const response = await api.get(`/loadouts/share/${shareCode}`);
    return response.data;
  },

  /**
   * Create a new loadout
   */
  async createLoadout(loadout: CreateLoadoutDto): Promise<UserLoadout> {
    const response = await api.post('/loadouts', loadout);
    return response.data;
  },

  /**
   * Update an existing loadout
   */
  async updateLoadout(loadoutId: number, updates: UpdateLoadoutDto): Promise<UserLoadout> {
    const response = await api.put(`/loadouts/${loadoutId}`, updates);
    return response.data;
  },

  /**
   * Delete a loadout
   */
  async deleteLoadout(loadoutId: number): Promise<void> {
    await api.delete(`/loadouts/${loadoutId}`);
  },

  /**
   * Get compatible items for component swapping
   */
  async getCompatibleItems(size: number, type: string = 'WeaponGun'): Promise<CompatibleItem[]> {
    const response = await api.get('/loadouts/compatible-items', {
      params: { size, type }
    });
    return response.data;
  }
};

// ============ Power Triangle Helpers ============

/**
 * Normalize pip values to ensure they sum to a valid total (typically 12 pips max)
 * When one pip increases, others decrease proportionally
 */
export function normalizePips(
  weapon: number,
  shield: number,
  engine: number,
  maxTotal: number = 12
): { weapon: number; shield: number; engine: number } {
  const total = weapon + shield + engine;
  
  if (total <= maxTotal) {
    return { weapon, shield, engine };
  }
  
  // Scale down proportionally
  const scale = maxTotal / total;
  return {
    weapon: Math.round(weapon * scale),
    shield: Math.round(shield * scale),
    engine: Math.round(engine * scale)
  };
}

/**
 * Convert pip count (0-4) to percentage (0-1) for curve calculations
 */
export function pipToPercentage(pips: number, maxPips: number = 4): number {
  return Math.max(0, Math.min(1, pips / maxPips));
}

/**
 * Get a description of the current pip allocation
 */
export function getPipDescription(weapon: number, shield: number, engine: number): string {
  const max = Math.max(weapon, shield, engine);
  
  if (weapon === shield && shield === engine) {
    return 'Balanced';
  }
  
  if (weapon === max && weapon > shield && weapon > engine) {
    return 'Attack Focus';
  }
  
  if (shield === max && shield > weapon && shield > engine) {
    return 'Defense Focus';
  }
  
  if (engine === max && engine > weapon && engine > shield) {
    return 'Speed Focus';
  }
  
  return 'Custom';
}

export default loadoutService;
